import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import { concatUrl, getCustomConfig, getNewAbortHandler } from "src/util/http/httpHelper"
import { ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import { IssuePriority, IssuePriorityFilterParams } from "src/util/types/dataModels/issuePriority"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_ISSUES,
} from "src/util/types/dataModels/user"

/**
 * Get the list of all Issue Priorities
 *
 * @param config custom configurations and filtering params
 */
export async function getIssuePrioritiesList(
  config: HttpConfig<IssuePriorityFilterParams> = {}
): Promise<ObjectList<IssuePriority>> {
  const entityURL = CSIurl.ISSUE_PRIORITIES
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create new Issue Priority
 *
 * @param config custom configurations and filtering params
 * @param priority the new Issue Priority
 */
export async function postIssuePriority(
  config: HttpConfig = {},
  priority: PayloadEntity<IssuePriority>
): Promise<IssuePriority> {
  const url = CSIurl.ISSUE_PRIORITIES
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, priority, { signal, customConfig })

  return data
}

/**
 * Get an individual Issue Priority
 *
 * @param config custom configurations and filtering params
 * @param priorityId
 */
export async function getIssuePriority(
  config: HttpConfig = {},
  priorityId: string
): Promise<IssuePriority> {
  const url = CSIurl.ISSUE_PRIORITIES_ID.replace("{id}", priorityId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Issue Priority
 *
 * @param config custom configurations and filtering params
 * @param priorityId the id of the Issue Priority that should be updated
 * @param priority the updated Issue Priority
 */
export async function putIssuePriority(
  config: HttpConfig = {},
  priorityId: string,
  priority: PayloadEntity<IssuePriority>
): Promise<IssuePriority> {
  const url = CSIurl.ISSUE_PRIORITIES_ID.replace("{id}", priorityId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, priority, { signal, customConfig })

  return data
}

/**
 * Partially update an individual Issue Priority
 *
 * @param config custom configurations and filtering params
 * @param priorityId the id of the Issue Priority that should be partially updated
 * @param priority the updated Issue Priority
 */
export async function patchIssuePriority(
  config: HttpConfig = {},
  priorityId: string,
  priority: Partial<PayloadEntity<IssuePriority>>
): Promise<IssuePriority> {
  const url = CSIurl.ISSUE_PRIORITIES_ID.replace("{id}", priorityId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ISSUES, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, priority, { signal, customConfig })

  return data
}
